import { apiClient } from './api';

/**
 * Where onboarding lives depends on which bundle this is.
 *
 * The platform serves it from UMS; the standalone CMS serves the same workflow
 * from case-management-service, whose RegisterOrganizationDto is deliberately
 * the same shape. Routing here rather than in the pages means the three-step
 * wizard, OnboardingGuard and the waiting/rejected screens are shared verbatim
 * between the two.
 */
const IS_CMS_BUNDLE = import.meta.env.VITE_APP_VARIANT === 'cms';

const ENDPOINTS = IS_CMS_BUNDLE
  ? { accessStatus: '/case/tenancy/access-status', register: '/case/tenancy/register' }
  : { accessStatus: '/ums/auth/access-status', register: '/ums/auth/register-organization' };

export type AccessState =
  | 'no_organization'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'suspended';

export interface AccessStatusResponse {
  state: AccessState;
  isPlatformAdmin: boolean;
  organization: {
    id: string;
    name: string;
    status: string;
  } | null;
  requiresReauthentication: boolean;
  rejectionReason?: string | null;
}

export interface RegisterOrganizationPayload {
  // Step 1: Organization Details
  orgName: string;
  orgSlug: string;
  sector: string;
  industry?: string;
  organizationSize: '1-50' | '51-200' | '201-1000' | '1001+';
  website?: string;
  // Org logo URL returned by uploadLogo() (e.g. "/api/ums/media/logos/...").
  logo?: string;

  // Step 2: Contact Information
  country: string;
  city: string;
  address: string;
  phone: string;
  contactPerson: string;
  contactRole: string;
  alternateContactName?: string;
  alternateContactRole?: string;
  alternateContactEmail?: string;
  description?: string;
  
  // Step 3: Review & Submit (acknowledgments handled on frontend)
}

interface RegisterOrganizationResponse {
  message: string;
  organizationId: string;
  organizationStatus: 'pending_approval';
}

interface AccessStatusApiResponse {
  state?: string;
  isPlatformAdmin?: boolean;
  organization?: {
    id?: string;
    name?: string;
    status?: string;
  } | null;
  requiresReauthentication?: boolean;
  rejectionReason?: string | null;
}

function normalizeState(state?: string): AccessState {
  const value = (state ?? '').toLowerCase();
  if (value === 'approved') return 'approved';
  if (value === 'pending_approval') return 'pending_approval';
  if (value === 'rejected') return 'rejected';
  if (value === 'suspended') return 'suspended';
  return 'no_organization';
}

export const onboardingService = {
  async getAccessStatus(): Promise<AccessStatusResponse> {
    const response = await apiClient.get<AccessStatusApiResponse>(ENDPOINTS.accessStatus);

    return {
      state: normalizeState(response?.state),
      isPlatformAdmin: Boolean(response?.isPlatformAdmin),
      organization: response?.organization
        ? {
            id: String(response.organization.id ?? ''),
            name: String(response.organization.name ?? 'Organization'),
            status: String(response.organization.status ?? 'PENDING'),
          }
        : null,
      requiresReauthentication: Boolean(response?.requiresReauthentication),
      rejectionReason: response?.rejectionReason?.trim() || null,
    };
  },

  async registerOrganization(
    payload: RegisterOrganizationPayload,
  ): Promise<RegisterOrganizationResponse> {
    return apiClient.post<RegisterOrganizationResponse>(ENDPOINTS.register, payload);
  },

  // Uploads a logo file and returns its server URL. Done separately from
  // registration so the (large) image never travels inside the JSON body.
  //
  // The CMS bundle has no media endpoint to upload to, so there the file is
  // read into a data URI and travels inside the registration JSON after all.
  // That is fine at this size — one row per tenant, written once — and is what
  // RegisterOrganizationDto.logo validates against.
  async uploadLogo(file: File): Promise<string> {
    if (IS_CMS_BUNDLE) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Could not read the selected image.'));
        reader.readAsDataURL(file);
      });
    }

    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ url: string }>(
      '/ums/media/logo',
      formData,
    );
    return response.url;
  },
};
